import { cn } from "cn";
import {
  AlertCircle,
  Check,
  CircleDashed,
  Clock,
  RefreshCw,
} from "lucide-react";
import type { FC, ReactNode } from "react";
import React from "react";

import type { Job } from "@core/domains/jobs";

import { DateFromNow } from "@react/foundation/display";
import { Button } from "@react/ui-primitives/button";

interface State {
  readonly icon: typeof Check;
  readonly iconClassName: string;
  readonly label: ReactNode;
  readonly labelClassName: string;
}

function getFailure(job: Job | undefined): string | undefined {
  switch (job?.status) {
    case "completed":
    case "pending":
    case "processing":
    case undefined:
      return undefined;
    case "failed":
      return job.error;
  }
}

function getState({
  isSending,
  job,
}: {
  readonly isSending: boolean;
  readonly job?: Job;
}): State {
  if (isSending) {
    return {
      icon: Clock,
      iconClassName: "text-muted-foreground",
      label: "Syncing…",
      labelClassName: "text-muted-foreground",
    };
  }

  if (!job) {
    return {
      icon: CircleDashed,
      iconClassName: "text-muted-foreground",
      label: "Never synced",
      labelClassName: "text-muted-foreground",
    };
  }

  switch (job.status) {
    case "completed":
      return {
        icon: Check,
        iconClassName: "text-success",
        label: <DateFromNow date={job.updatedAt} />,
        labelClassName: "text-muted-foreground",
      };
    case "failed":
      return {
        icon: AlertCircle,
        iconClassName: "text-destructive",
        label: <DateFromNow date={job.updatedAt} />,
        labelClassName: "text-muted-foreground",
      };
    case "pending":
      return {
        icon: Clock,
        iconClassName: "text-muted-foreground",
        label: "Syncing…",
        labelClassName: "text-muted-foreground",
      };
    case "processing":
      return {
        icon: RefreshCw,
        iconClassName: "animate-spin text-primary",
        label: "Syncing…",
        labelClassName: "font-medium text-primary",
      };
  }
}

interface JobSyncStatusProps {
  readonly isSending: boolean;
  readonly job?: Job;
  readonly onSync: () => void;
}

export const JobSyncStatus: FC<JobSyncStatusProps> = ({
  isSending,
  job,
  onSync,
}) => {
  const {
    icon: Icon,
    iconClassName,
    label,
    labelClassName,
  } = getState({ isSending, job });

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn("text-sm whitespace-nowrap", labelClassName)}
        title={getFailure(job)}
      >
        {label}
      </span>
      <Button
        disabled={
          isSending || job?.status === "pending" || job?.status === "processing"
        }
        onClick={onSync}
        size="icon-sm"
        variant="ghost"
      >
        <Icon className={iconClassName} />
      </Button>
    </div>
  );
};
